import { ExcelButton } from "@/controls/buttons/Excel-button";
import { HeatSearch } from "@/controls/heatSearch/components/HeatSearchTextField";
import { ATHENS_GRAY } from "@/controls/shared/constants/colors";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	ShiftInfo,
	getMomentTz,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Box, Grid2 as Grid, Paper, Typography } from "@mui/material";
import { ProcessCellForExportParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ProductionManagementTable } from "./components/ProductionManagementTable";
import { useProductionManagementContext } from "./context/ProductionManagementContext";
import { ProductionDataRow } from "./models/ProductionDataModel";
import {
	getLastestHeatsWithLadleClose,
	getProductionData,
	searchHeatsWithLadleClose,
} from "./repositories/productionManagementRepository";
const moment = getMomentTz();

export const ProductionManagementPage = () => {
	const navigate = useNavigate();
	const {
		state: { lastestHeats },
		actions: { setLastestHeats, setIsLoading, setError },
	} = useProductionManagementContext();

	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const shiftDate = moment(shiftInfo?.CurrentStart).format("MM-DD-YYYY");

	const [productionData, setProductionData] = useState<ProductionDataRow[]>([]);
	const [error, setErrorState] = useState<string>("");
	const [isLoading, setIsLoadingState] = useState<boolean>(true);

	const [firstRender, setFirstRender] = useState(true);
	const { shiftId: shiftIdParam } = useParams();
	const gridRef = useRef<AgGridReact>(null);

	const handleContainerContextMenu = (
		event: React.MouseEvent<HTMLDivElement, MouseEvent>,
	) => {
		event.preventDefault();
	};

	const getProductionDataFromAPI = async (shift?: ShiftInfo) => {
		setIsLoadingState(true);
		setIsLoading(true);
		const shiftId = get(shift ? shift : shiftInfo, "CurrentShiftId", null);
		const productionResp = await getProductionData(Number(shiftId));
		if (productionResp.ok) {
			setProductionData(productionResp.data.tables[0].rows);
		} else {
			setErrorState(productionResp.message);
			setError(productionResp.message);
		}
		setIsLoadingState(false);
		setIsLoading(false);
	};

	const getLastestHeatsFromAPI = async () => {
		const lastestHeatsResp = await getLastestHeatsWithLadleClose(false);
		if (lastestHeatsResp.ok) {
			const data = get(lastestHeatsResp, "data.tables[0].rows", null);
			setLastestHeats(data);
		} else {
			setErrorState(lastestHeatsResp.message);
			setError(lastestHeatsResp.message);
		}
	};

	const getHeats = async (searchTerm: string) => {
		if (searchTerm.length >= 3) {
			const searchHeatsResp = await searchHeatsWithLadleClose(
				searchTerm,
				false,
			);
			if (searchHeatsResp.ok) {
				const data = get(searchHeatsResp, "data.tables[0].rows", null);
				return data;
			} else {
				setErrorState(searchHeatsResp.message);
				setError(searchHeatsResp.message);
				return [];
			}
		} else {
			return lastestHeats;
		}
	};

	const handleExportToExcel = () => {
		if (gridRef.current && gridRef.current.api) {
			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: `Production-Management-${shiftDate}.xlsx`,
				sheetName: "Production Management",
				processCellCallback: (params: ProcessCellForExportParams) => {
					const val = params.value;
					return val ?? "";
				},
			};
			gridRef.current.api.exportDataAsExcel(params);
		} else {
			setErrorState("Unable to export data. Please try again.");
			setError("Unable to export data. Please try again.");
		}
	};

	useEffect(() => {
		if (shiftInfo && !firstRender) {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			navigate(`/production-management/${shiftId}`);

			(async () => {
				await getProductionDataFromAPI();
				await getLastestHeatsFromAPI();
			})();
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (shiftIdParam) {
			(async () => {
				const shiftId = Number(shiftIdParam);
				getShiftDataFromAPI(null, shiftId).then(async (shift) => {
					if (shift) {
						getProductionDataFromAPI(shift).then(() => {
							getLastestHeatsFromAPI();
							setFirstRender(false);
						});
					}
				});
			})();
		} else {
			(async () => {
				getShiftDataFromAPI(null, 0).then(async (shift) => {
					if (shift) {
						getProductionDataFromAPI(shift).then(() => {
							getLastestHeatsFromAPI();
							setFirstRender(false);
						});
					}
				});
			})();
		}
	}, []);

	return (
		<>
			<HelmetDexteel title={`Production Management`} />
			<div onContextMenu={handleContainerContextMenu}>
				<Paper
					elevation={0}
					style={{
						width: "100%",
						padding: "15px 20px 10px",
						height: "93vh",
						backgroundColor: ATHENS_GRAY,
					}}
				>
					<Grid container justifyContent="center" alignItems="center">
						<Grid size={{ md: 12, xs: 12 }} style={{ margin: 0 }}>
							<Grid size={{ md: 12, xs: 12 }}>
								<Grid
									container
									direction="row"
									justifyContent="space-between"
									alignItems="center"
								>
									<Typography variant="h5" style={{ fontWeight: 600 }}>
										Production Management
									</Typography>
								</Grid>
							</Grid>
							<Grid size={{ md: 12, xs: 12 }} style={{ marginBottom: "10px" }}>
								<Grid
									container
									direction="row"
									justifyContent="flex-start"
									alignItems="stretch"
									spacing={1}
								>
									<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
										<Paper
											elevation={1}
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<ShiftDayNavigatorControl preventInit withPaper={false} />
										</Paper>
									</Grid>
									<Grid size={{ xs: "auto" }}>
										<Paper
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												minWidth: "200px",
											}}
										>
											<Box sx={{ p: 1 }}>
												<Grid
													container
													spacing={2}
													justifyContent="center"
													alignItems="center"
												>
													<Grid size={{ xs: 12 }}>
														<HeatSearch
															heats={lastestHeats}
															getHeats={getHeats}
															loading={isLoading}
															onBlur={() => {}}
															onChange={(heat) => {
																const newSearchParams = new URLSearchParams();
																// if (heat.ProcessId) {
																// 	newSearchParams.set(
																// 		"processId",
																// 		heat.ProcessId.toString(),
																// 	);
																// }
																const queryString = newSearchParams.toString();
																window.location.href = `/production-management/${heat.ShiftId}?${queryString}`;
															}}
														/>
													</Grid>
												</Grid>
											</Box>
										</Paper>
									</Grid>
									<Grid
										size={{ xs: "auto" }}
										style={{
											display: "flex",
											alignItems: "center",
											marginLeft: "auto",
										}}
									>
										<ExcelButton onClick={handleExportToExcel} />
									</Grid>
								</Grid>
							</Grid>
							<ProductionManagementTable
								isLoading={isLoading}
								getProductionData={getProductionDataFromAPI}
								gridRef={gridRef}
								rows={productionData}
							/>
						</Grid>
					</Grid>
				</Paper>
				<ErrorModal error={error} onHide={() => setErrorState("")} />
			</div>
		</>
	);
};
